import { Filter, FilterOption } from '@/components/DesignSystem/Toolbar/interfaces';
import { CustomerChurn } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { getChurnStatusFilter } from '@/utilities/ChurnCustomer';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { UserType } from '@shared/enums';
import { SharedConfig } from '@shared/SharedConfig';
import { getFarmerName } from '@shared/utilities';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useGetRetailerLocations } from './useRetailerLocations';
import { useSearch } from './useSearch';

export const useInsightsChurnFilters = (program: ApiRewardsProgram, retailerId: string) => {
  const {
    debouncedSearch: debouncedCustomerSearch,
    search: customerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();

  const { data: filterOptions } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW_FILTER_OPTIONS, retailerId],
    () => RetailerApi.getPointsOverviewFilterOptions(retailerId),
  );

  const { data: filterCustomers } = useQuery(
    [QueryKeys.LIST_USERS, retailerId, debouncedCustomerSearch],
    async () => {
      const res = await UserApi.list(
        {
          limit: 20,
          retailerId,
          search: debouncedCustomerSearch,
          userType: [UserType.Farmer],
        },
      );
      return res.data;
    },
  );

  const { retailerLocations } = useGetRetailerLocations(
    retailerId,
    { limit: SharedConfig.maxPageLimit },
  );

  const filters: Filter[] = useMemo(() => {

    const locationFilterOptions: FilterOption[] = retailerLocations?.data.map((location) => ({
      id: location.id,
      label: location.name,
    })) ?? [];

    const tierOptions = program?.tiers?.map(
      (tier) => ({ id: tier.rank.toString(), label: tier.name }),
    ) ?? [];

    return [
      {
        id: 'customer',
        isChipLabelVerbose: true,
        label: CustomerChurn.filters['customer'],
        onSearch: (search) => setCustomerSearch(search),
        options: filterCustomers?.map((customer) => ({
          id: customer.id,
          label: getFarmerName(customer) || (customer.externalId ?? ''),
        })) ?? [],
        search: customerSearch,
        selectionMethod: 'multi-select',
      },
      {
        id: 'current-status',
        isChipLabelVerbose: true,
        label: CustomerChurn.filters['current-status'],
        options: [{ id: '-1', label: 'None' }, ...tierOptions],
        selectionMethod: 'multi-select',
      },
      ...[getChurnStatusFilter()],
      {
        id: 'salesperson',
        isChipLabelVerbose: true,
        label: CustomerChurn.filters['salesperson'],
        options: filterOptions?.salespersons ?? [],
        selectionMethod: 'multi-select',
      },
      {
        id: 'location',
        isChipLabelVerbose: true,
        label: CustomerChurn.filters['location'],
        options: locationFilterOptions,
        selectionMethod: 'multi-select',
      },
    ];
  }, [
    customerSearch,
    filterCustomers,
    filterOptions,
    program.tiers,
    retailerLocations?.data,
    setCustomerSearch,
  ]);

  return { filters };
};
